import React from "react";
import CallServiceTable from "../../../components/Tables/CallServiceTable";
import { Button } from "../../../components/Buttons";
import { useModal } from "../../../contexts/ModalContext";
import { useDialog } from "../../../contexts/DialogContext";
import { UploadDocuments } from "../../../containers/menu/providers/UploadDocuments";
import FormDefault from "../../../components/Forms/FormDefault";
import { requestAuth } from "../../../components/services/RequestService";
import { toast } from "react-toastify";
import { H3 } from "../../../components/Text";
import { DialogConfirmation } from "../../../components/Modal";

const ProviderDetails = ({ item, onClose }) => {
  const data = item;
  const isTransport =
    data.type === "transporte" ||
    !!data.informacion_legal ||
    !!data.tipo_transporte;

  const renderValue = (val) => {
    if (!val) return "-";
    if (typeof val === "object") {
      if (val.nombre || val.cargo) {
        return `${val.nombre || ""} ${val.cargo ? `- ${val.cargo}` : ""}`;
      }
      return JSON.stringify(val);
    }
    return val;
  };

  const renderContactos = (val) => {
    if (!val) return "Sin información de contactos";
    if (typeof val === "string") return val;
    if (Array.isArray(val)) {
      return val
        .map((c) =>
          typeof c === "object"
            ? `${c.nombre || c.name || ""} ${c.cargo || c.position ? `(${c.cargo || c.position})` : ""}`
            : String(c),
        )
        .join("\n");
    }
    if (typeof val === "object") {
      return `${val.nombre || val.name || ""} ${val.cargo || val.position ? `(${val.cargo || val.position})` : ""}`;
    }
    return String(val);
  };

  return (
    <div className="p-6 h-full overflow-y-auto">
      <div className="flex justify-between items-center mb-6">
        <H3 className="text-2xl font-bold text-gray-800">
          Detalles de {isTransport ? "Transportadora" : "Proveedor"}
        </H3>
        <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
          <i className="fas fa-times text-xl"></i>
        </button>
      </div>

      <div className="space-y-6">
        <section>
          <h4 className="text-lg font-semibold text-blue-600 border-b pb-1 mb-3">
            Información General
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
            <DetailItem label="Nombre" value={data.name} />
            <DetailItem
              label="Código TR4"
              value={data.codigo_tr4 || data.current_code}
            />
            <DetailItem
              label={isTransport ? "Tipo de Transporte" : "Tipo de Proveedor"}
              value={data.tipo_transporte || data.tipo_proveedor || data.adq_ie}
            />
            <DetailItem
              label="Dirección"
              value={data.direccion || data.address}
            />
            <DetailItem
              label="NIT / TAX ID / RUC"
              value={data.nit_tax_id || data.nit_ruc || data.nit}
            />
            <DetailItem label="País" value={data.pais || data.country} />
            <DetailItem label="Ciudad" value={data.ciudad || data.city} />
            <DetailItem label="Email" value={data.email} />
            <DetailItem
              label="Página Web"
              value={data.pagina_web || data.web_page}
            />
            <DetailItem label="Teléfono" value={data.telefono || data.phone} />
          </div>
        </section>

        <section>
          <h4 className="text-lg font-semibold text-blue-600 border-b pb-1 mb-3">
            Contactos
          </h4>
          <div className="bg-gray-50 p-3 rounded-lg border">
            <p className="whitespace-pre-wrap text-gray-700">
              {renderContactos(data.contactos || data.contact)}
            </p>
          </div>
        </section>

        {isTransport && data.informacion_legal && (
          <section>
            <h4 className="text-lg font-semibold text-blue-600 border-b pb-1 mb-3">
              Información Legal
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
              <DetailItem
                label="Matrícula Comercio"
                value={data.informacion_legal.matricula_comercio}
              />
              <DetailItem
                label="Representante Legal"
                value={data.informacion_legal.representante_legal}
              />
              <DetailItem
                label="CI Representante"
                value={data.informacion_legal.ci_representante}
              />
              <DetailItem
                label="Dirección Representante"
                value={data.informacion_legal.direccion_representante}
              />
              <DetailItem
                label="Testimonio"
                value={data.informacion_legal.testimonio}
              />
              <DetailItem
                label="Fecha Testimonio"
                value={data.informacion_legal.fecha_testimonio}
              />
              <DetailItem
                label="Notaría"
                value={data.informacion_legal.notaria}
              />
              <DetailItem
                label="Número Notaría"
                value={data.informacion_legal.numero_notaria}
              />
              <DetailItem
                label="Domicilio Notaría"
                value={data.informacion_legal.domicilio_notaria}
              />
              <DetailItem
                label="Ciudad Notaría"
                value={data.informacion_legal.ciudad_notaria}
              />
            </div>
          </section>
        )}
      </div>

      <div className="mt-8 flex justify-end">
        <Button onClick={onClose} className="px-8 shadow-md">
          Cerrar
        </Button>
      </div>
    </div>
  );
};

const DetailItem = ({ label, value }) => {
  const renderValue = (val) => {
    if (!val) return "-";
    if (typeof val === "object") {
      if (val.nombre || val.cargo) {
        return `${val.nombre || ""} ${val.cargo ? `- ${val.cargo}` : ""}`;
      }
      return JSON.stringify(val);
    }
    return val;
  };

  return (
    <div className="flex flex-col">
      <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
        {label}
      </span>
      <span className="text-gray-900 font-medium">{renderValue(value)}</span>
    </div>
  );
};

const Providers = () => {
  const { openModal, onClose } = useModal();
  const { openDialog, dialogClose } = useDialog();

  function handleNewProvider(reload, mode = "normal") {
    openModal(<FormProvider mode={mode} onClose={onClose} onReload={reload} />);
  }

  function handleEditProvider(item, reload) {
    openModal(<FormProvider item={item} onClose={onClose} onReload={reload} />);
  }

  function handleAddDocuments(item, reload) {
    openDialog(
      <UploadDocuments item={item} onClose={dialogClose} onReload={reload} />,
    );
  }

  function handleShowDetails(item) {
    openModal(<ProviderDetails item={item} onClose={onClose} />);
  }

  function handleDelete(item, reload) {
    openDialog(
      <DialogConfirmation
        children={<H3>¿Estas seguro que quieres eliminar el proveedor?</H3>}
        onClose={dialogClose}
        method="delete"
        url={`/Dropdown/Option/${item.id}`}
        texBtn="Eliminar"
        handleSuccess={reload}
      />,
    );
  }

  function handleProcessData(data) {
    return data?.map((item) => {
      var dataObj = JSON.parse(item.data);
      return {
        ...item,
        name: item.name,
        ...dataObj,
      };
    });
  }

  return (
    <CallServiceTable
      urlApi="/Dropdown/providers/options"
      onClick={(item) => handleShowDetails(item)}
      component={({ reload }) => {
        return (
          <div className="flex gap-4">
            <Button
              className="text-lg py-3 flex items-center gap-2"
              onClick={() => handleNewProvider(reload, "normal")}
            >
              Crear proveedor <i className="fa-solid fa-plus"></i>
            </Button>
            <Button
              className="text-lg py-3 flex items-center gap-2 bg-[#059669] hover:bg-[#047857]"
              onClick={() => handleNewProvider(reload, "transport")}
            >
              Crear transportadora <i className="fa-solid fa-truck"></i>
            </Button>
          </div>
        );
      }}
      handleProcessData={(data) => handleProcessData(data)}
      filters={[
        {
          type: "select",
          name: "name",
          url: "/Dropdown/providers/options",
          default: "Todas los proveedores",
          valueOption: "name",
          labelOption: "name",
        },
      ]}
      header={[
        { name: "name", label: "Nombre" },
        {
          name: "type_display",
          label: "Tipo",
          type: "custom",
          builder: (val, _, head) => {
            const isTransport =
              head.type === "transporte" || !!head.tipo_transporte;
            return (
              <div
                className={`px-3 py-1 rounded-full text-xs font-medium w-fit ${
                  isTransport
                    ? "bg-green-100 text-green-800"
                    : "bg-blue-100 text-blue-800"
                }`}
              >
                {isTransport
                  ? `Transporte: ${head.tipo_transporte || ""}`
                  : head.tipo_proveedor || head.adq_ie || "Proveedor"}
              </div>
            );
          },
        },
        { name: "codigo_tr4", label: "Codigo TR4" },
        { name: "direccion", label: "Direccion" },
        { name: "nit_tax_id", label: "NIT/RUC/TAX ID" },
        { name: "pais", label: "País" },
        { name: "ciudad", label: "Ciudad" },
        { name: "email", label: "Email" },
        { name: "telefono", label: "Telefono" },
        {
          name: "actions",
          label: "Acciones",
          sticky: true,
          type: "action",
          actions: [
            {
              label: "Ver Detalles",
              icon: "fas fa-eye",
              action: handleShowDetails,
              color: "text-[#1d4ed8]",
            },
            {
              label: "Editar",
              icon: "fas fa-edit",
              action: handleEditProvider,
              color: "text-[#1d4ed8]",
            },
            {
              label: "Subir documentos",
              icon: "fas fa-folder-open",
              action: handleAddDocuments,
              color: "text-[#1d4ed8]",
            },
            {
              label: "Eliminar",
              icon: "fas fa-trash",
              action: handleDelete,
              color: "text-red-500",
            },
          ],
        },
      ]}
    />
  );
};

const FormProvider = ({ item = null, mode = "normal", onClose, onReload }) => {
  const isTransport = item
    ? item.type === "transporte" ||
      !!item.informacion_legal ||
      !!item.tipo_transporte
    : mode === "transport";

  const formatContactosInitial = (val) => {
    if (!val) return "";
    if (typeof val === "string") return val;
    if (Array.isArray(val)) {
      return val
        .map((c) =>
          typeof c === "object"
            ? `${c.nombre || c.name || ""} ${
                c.cargo || c.position ? `- ${c.cargo || c.position}` : ""
              }`
            : String(c),
        )
        .join("\n");
    }
    if (typeof val === "object") {
      return `${val.nombre || val.name || ""} ${
        val.cargo || val.position ? `- ${val.cargo || val.position}` : ""
      }`;
    }
    return String(val);
  };

  const processContactosOnSubmit = (text) => {
    if (!text) return [];
    if (typeof text !== "string") return text;
    const lines = text.split("\n").filter((l) => l.trim() !== "");
    return lines.map((line) => {
      const parts = line.split(" - ");
      if (parts.length >= 2) {
        return {
          nombre: parts[0].trim(),
          cargo: parts.slice(1).join(" - ").trim(),
        };
      }
      return { nombre: line.trim(), cargo: "" };
    });
  };

  async function onSubmit(values) {
    try {
      const existingData = item?.data ? JSON.parse(item.data) : {};

      const dataObj = {
        ...existingData,
        codigo_tr4: values.codigo_tr4,
        direccion: values.direccion,
        pais: values.pais,
        ciudad: values.ciudad,
        email: values.email,
        pagina_web: values.pagina_web,
        telefono: values.telefono,
        contactos: processContactosOnSubmit(values.contactos),
      };

      if (isTransport) {
        dataObj.tipo_transporte = values.tipo_transporte;
        dataObj.nit_ruc = values.nit_ruc;
        dataObj.informacion_legal = {
          matricula_comercio: values.matricula_comercio,
          representante_legal: values.representante_legal,
          ci_representante: values.ci_representante,
          direccion_representante: values.direccion_representante,
          testimonio: values.testimonio,
          fecha_testimonio: values.fecha_testimonio,
          notaria: values.notaria,
          numero_notaria: values.numero_notaria,
          domicilio_notaria: values.domicilio_notaria,
          ciudad_notaria: values.ciudad_notaria,
        };
      } else {
        dataObj.tipo_proveedor = values.tipo_proveedor;
        dataObj.nit_tax_id = values.nit_tax_id;
      }

      const payload = {
        DropdownListLabel: "providers",
        data: JSON.stringify(dataObj),
        name: values.name,
        type: isTransport ? "transporte" : "proveedor",
      };

      await requestAuth(
        item ? "put" : "post",
        item ? `/Dropdown/Option/${item.id}` : "/Dropdown/Option",
        payload,
      );

      toast.success(
        `${isTransport ? "Transportadora" : "Proveedor"} ${item ? "actualizado" : "creado"} correctamente`,
      );
      onReload();
      onClose();
    } catch (error) {
      console.error(error);
      toast.error("Error al procesar la solicitud");
    }
  }

  const commonFields = [
    { name: "name", label: "Nombre", type: "text", validate: true },
    { name: "codigo_tr4", label: "Código TR4", type: "text" },
    { name: "direccion", label: "Dirección", type: "text" },
    { name: "pais", label: "País", type: "text" },
    { name: "ciudad", label: "Ciudad", type: "text" },
    { name: "email", label: "Email", type: "email" },
    { name: "pagina_web", label: "Página Web", type: "text" },
    { name: "telefono", label: "Teléfono", type: "text" },
    { name: "contactos", label: "Contactos", type: "textarea" },
  ];

  const providerFields = [
    {
      name: "tipo_proveedor",
      label: "Tipo de proveedor",
      type: "select",
      typeInput: "select",
      options: [
        { value: "INTERNACIONAL", label: "INTERNACIONAL" },
        { value: "LOCAL", label: "LOCAL" },
      ],
      labelOption: "label",
      value: "value",
    },
    { name: "nit_tax_id", label: "NIT / TAX ID", type: "text" },
  ];

  const transportFields = [
    {
      name: "tipo_transporte",
      label: "Tipo de transporte",
      type: "select",
      typeInput: "select",
      options: [
        { value: "TERRESTRE", label: "TERRESTRE" },
        { value: "AEREO", label: "AEREO" },
        { value: "MARITIMO", label: "MARITIMO" },
      ],
      labelOption: "label",
      value: "value",
    },
    { name: "nit_ruc", label: "NIT / RUC", type: "text" },
    { name: "matricula_comercio", label: "Matrícula Comercio", type: "text" },
    { name: "representante_legal", label: "Representante Legal", type: "text" },
    { name: "ci_representante", label: "CI Representante", type: "text" },
    {
      name: "direccion_representante",
      label: "Dirección Representante",
      type: "text",
    },
    { name: "testimonio", label: "Testimonio", type: "text" },
    { name: "fecha_testimonio", label: "Fecha Testimonio", type: "date" },
    { name: "notaria", label: "Notaría", type: "text" },
    { name: "numero_notaria", label: "Número Notaría", type: "text" },
    { name: "domicilio_notaria", label: "Domicilio Notaría", type: "text" },
    { name: "ciudad_notaria", label: "Ciudad Notaría", type: "text" },
  ];

  const fields = isTransport
    ? [
        commonFields[0],
        transportFields[0],
        transportFields[1],
        ...commonFields.slice(1),
        ...transportFields.slice(2),
      ]
    : [commonFields[0], ...providerFields, ...commonFields.slice(1)];

  return (
    <FormDefault
      tittle={`Formulario de ${item ? "editar" : "crear"} ${isTransport ? "transportadora" : "proveedor"}`}
      fields={fields}
      initialValues={{
        name: item?.name || "",
        codigo_tr4: item?.codigo_tr4 || item?.current_code || "",
        direccion: item?.direccion || item?.address || "",
        pais: item?.pais || item?.country || "",
        ciudad: item?.ciudad || item?.city || "",
        email: item?.email || "",
        pagina_web: item?.pagina_web || item?.web_page || "",
        telefono: item?.telefono || item?.phone || "",
        contactos: formatContactosInitial(item?.contactos || item?.contact),
        tipo_proveedor: item?.tipo_proveedor || item?.adq_ie || "",
        nit_tax_id: item?.nit_tax_id || item?.nit || "",
        tipo_transporte: item?.tipo_transporte || "",
        nit_ruc: item?.nit_ruc || "",
        matricula_comercio: item?.informacion_legal?.matricula_comercio || "",
        representante_legal: item?.informacion_legal?.representante_legal || "",
        ci_representante: item?.informacion_legal?.ci_representante || "",
        direccion_representante:
          item?.informacion_legal?.direccion_representante || "",
        testimonio: item?.informacion_legal?.testimonio || "",
        fecha_testimonio: item?.informacion_legal?.fecha_testimonio || "",
        notaria: item?.informacion_legal?.notaria || "",
        numero_notaria: item?.informacion_legal?.numero_notaria || "",
        domicilio_notaria: item?.informacion_legal?.domicilio_notaria || "",
        ciudad_notaria: item?.informacion_legal?.ciudad_notaria || "",
      }}
      onClose={onClose}
      onSubmit={onSubmit}
      buttonName="Registrar"
    />
  );
};

export default Providers;
